// FidelityPanelMockup.jsx
// -----------------------------------------------------------------------------
// Inline VA-facing fidelity warning panel — DEV-ONLY mockup.
//
// Spec: docs/journal/PHASE_4D_VA_FIDELITY_GATE_DESIGN.md §4 (UX) and §6 (server
// contract). This component is pure-presentational; it renders any of the
// fidelity states defined in mockFidelityStates.js without any server calls
// or persistence.
//
// In the prototype phase this component will be lifted out of `/dev/...` and
// driven by the real POST /api/voice-snippets/score response. The prop shape
// matches the response shape (`score`) so the component contract holds.
//
// Props:
//   promptText : string  — the exact prompt text shown to the VA
//   score      : object  — the response from POST /api/voice-snippets/score
//                          (see §6.1 of the design doc), or a 'PENDING' /
//                          'GRAY' shape with nulls
//   onReRecord : ()=>void                 — fires when VA clicks Re-record
//   onSaveSubmit : ({ vaReason })=>void   — fires when VA clicks Confirm
//
// State (component-local):
//   acknowledging : bool   — true once the VA has expanded "Save & Submit anyway"
//   vaReason      : string — content of the optional/required reason field
// -----------------------------------------------------------------------------

import React, { useState, useMemo } from 'react';
import './fidelityPanel.css';

// -----------------------------------------------------------------------------
// Plain-English translation of detector tags + diff-derived issue descriptions.
// Per design §4.1 we surface symptoms only, never editorialize.
// -----------------------------------------------------------------------------
const FLAG_LABELS = {
  low_confidence_word: 'Quiet or mumbled word',
  filler_words: null, // intentionally suppressed — already counted in deriveDiffIssues as "Filler words (N)"
  long_pause: null,   // intentionally suppressed — surfaced as "Long pause(s) (N)" via fidelityBreakdown.longPauseCount
  false_start: 'False start at the beginning',
  mid_restart: 'Brief restart mid-line',
  energy_dropout: 'Energy dropout',
};

const deriveDiffIssues = (diff) => {
  if (!Array.isArray(diff)) return [];
  const counts = { ins: 0, del: 0, sub: 0, filler: 0 };
  diff.forEach((d) => {
    if (counts[d.type] !== undefined) counts[d.type] += 1;
  });
  const issues = [];
  if (counts.ins > 0) issues.push(`Words added (${counts.ins})`);
  if (counts.del > 0) issues.push(`Words missing (${counts.del})`);
  if (counts.sub > 0) issues.push(`Words swapped (${counts.sub})`);
  // Fillers ("um", "uh", "er", "ah", "hmm") are surfaced as their own
  // issue line so the VA knows they were detected without being told they
  // were "wrong". The server already removed them from the WER score; this
  // label is informational. See FidelityScoringService.js §Filler-word
  // detection for the underlying logic.
  if (counts.filler > 0) issues.push(`Filler words (${counts.filler})`);
  return issues;
};

const deriveFlagIssues = (flags) => {
  if (!Array.isArray(flags)) return [];
  // Dedupe — the v1 detector can emit `low_confidence_word` multiple times
  // (once per word). We surface it once.
  const unique = Array.from(new Set(flags));
  return unique
    .map((f) => (f in FLAG_LABELS ? FLAG_LABELS[f] : f))
    .filter((label) => label !== null && label !== undefined);
};


// -----------------------------------------------------------------------------
// Band-specific copy. Symptoms only — no admonition.
// -----------------------------------------------------------------------------
const BAND_META = {
  GREEN: {
    icon: '✓',
    label: 'GREEN',
    glyph: '🟢',
    headline: 'Clean take',
    sublabel: null,
    accentClass: 'aai-fp-band--green',
    saveLabel: 'Save & Submit',
    reasonRequired: false,
    canSubmit: true,
  },
  AMBER: {
    icon: '!',
    label: 'AMBER',
    glyph: '🟡',
    headline: 'Take diverges from script',
    sublabel: 'Issues detected:',
    accentClass: 'aai-fp-band--amber',
    saveLabel: 'Save & Submit anyway',
    reasonRequired: false,
    canSubmit: true,
  },
  RED: {
    icon: '!',
    label: 'RED',
    glyph: '🔴',
    headline: 'This take significantly diverges from the script',
    sublabel: 'Issues detected:',
    accentClass: 'aai-fp-band--red',
    saveLabel: 'Save & Submit anyway',
    reasonRequired: true,
    canSubmit: true,
  },
  GRAY: {
    icon: '–',
    label: 'GRAY',
    glyph: '⚪',
    headline: 'Scoring unavailable',
    sublabel: 'Submit at your own discretion.',
    accentClass: 'aai-fp-band--gray',
    saveLabel: 'Save & Submit',
    reasonRequired: false,
    canSubmit: true,
  },
  PENDING: {
    icon: '…',
    label: 'PENDING',
    glyph: '⌛',
    headline: 'Scoring your take…',
    sublabel: null,
    accentClass: 'aai-fp-band--pending',
    saveLabel: 'Save & Submit',
    reasonRequired: false,
    canSubmit: false,
  },
};

// -----------------------------------------------------------------------------
// Diff render — two stacked rows of word tokens. Type-class drives styling.
// -----------------------------------------------------------------------------
const DiffView = ({ diff }) => {
  if (!Array.isArray(diff) || diff.length === 0) return null;

  // Build two parallel arrays — script (prompt) row + heard (transcript) row —
  // each cell aligned to the same column index. `ins` entries leave a blank
  // in the script row; `del` entries leave a blank in the heard row.
  return (
    <div className="aai-fp-diff" role="group" aria-label="Word-level diff">
      <div className="aai-fp-diff__row aai-fp-diff__row--script">
        <span className="aai-fp-diff__rowlabel">Script</span>
        {diff.map((d, idx) => (
          <span
            key={`s-${idx}`}
            className={`aai-fp-diff__token aai-fp-diff__token--${d.type}`}
          >
            {d.promptWord ?? '·'}
          </span>
        ))}
      </div>
      <div className="aai-fp-diff__row aai-fp-diff__row--heard">
        <span className="aai-fp-diff__rowlabel">Heard</span>
        {diff.map((d, idx) => (
          <span
            key={`h-${idx}`}
            className={`aai-fp-diff__token aai-fp-diff__token--${d.type}`}
          >
            {d.transcribedWord ?? '·'}
          </span>
        ))}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Score badge — band glyph + numeric score (or `…` / `–`).
// -----------------------------------------------------------------------------
const ScoreBadge = ({ band, score }) => {
  const meta = BAND_META[band] || BAND_META.GRAY;
  return (
    <span
      className={`aai-fp-badge ${meta.accentClass}`}
      role="status"
      aria-label={`Fidelity band ${meta.label}${score != null ? `, score ${score}` : ''}`}
    >
      <span className="aai-fp-badge__glyph" aria-hidden>{meta.glyph}</span>
      <span className="aai-fp-badge__label">{meta.label}</span>
      <span className="aai-fp-badge__sep" aria-hidden>·</span>
      <span className="aai-fp-badge__score">
        {score != null ? score : (band === 'PENDING' ? '…' : '–')}
      </span>
    </span>
  );
};

// -----------------------------------------------------------------------------
// The panel.
// -----------------------------------------------------------------------------
const FidelityPanelMockup = ({
  promptText,
  score,
  onReRecord = () => { },
  onSaveSubmit = () => { },
}) => {
  const band = score?.fidelityBand || 'GRAY';
  const meta = BAND_META[band] || BAND_META.GRAY;

  const [acknowledging, setAcknowledging] = useState(false);
  const [vaReason, setVaReason] = useState('');

  const issues = useMemo(() => {
    const diffIssues = deriveDiffIssues(score?.transcriptDiff);
    const flagIssues = deriveFlagIssues(score?.fidelityBreakdown?.acousticFlags);
    // v1.6: dropped-filler / long-hesitation detection from word-timestamp
    // gaps. We surface this *outside* the transcriptDiff (because the gap is
    // not a word — it's the *absence* of a word) but next to the diff issues
    // since the VA experiences it the same way: "something happened at this
    // spot in my take." Suppressed at the flag layer to avoid double-listing.
    const longPauseCount = score?.fidelityBreakdown?.longPauseCount || 0;
    const pauseIssues = longPauseCount > 0
      ? [longPauseCount === 1 ? 'Long pause / dropped audio (1)' : `Long pauses / dropped audio (${longPauseCount})`]
      : [];
    return [...diffIssues, ...pauseIssues, ...flagIssues];
  }, [score]);

  // Save button enablement:
  //   GREEN / GRAY → single click → Confirm immediately
  //   AMBER / RED  → first click expands acknowledgement; Confirm requires
  //                  reason field (required on RED, optional on AMBER)
  //   PENDING      → disabled
  const needsAcknowledge = band === 'AMBER' || band === 'RED';
  const reasonRequired = meta.reasonRequired;
  const confirmDisabled =
    !meta.canSubmit ||
    (acknowledging && reasonRequired && vaReason.trim().length === 0);

  const handleSavePrimary = () => {
    if (!meta.canSubmit) return;
    if (needsAcknowledge && !acknowledging) {
      setAcknowledging(true);
      return;
    }
    onSaveSubmit({ vaReason: needsAcknowledge ? vaReason.trim() : null });
  };

  // GREEN gets a compact one-line affordance. Everything else gets the full
  // panel with diff + issues + actions.
  const isCompact = band === 'GREEN' || band === 'PENDING';

  return (
    <section
      className={`aai-fp ${meta.accentClass} ${isCompact ? 'aai-fp--compact' : ''}`}
      aria-label="Fidelity feedback"
    >
      {/* PROMPT — always visible, never moves */}
      <div className="aai-fp__prompt">
        <span className="aai-fp__promptlabel">Script</span>
        <p className="aai-fp__prompttext">{promptText}</p>
      </div>

      {/* HEADER — badge + headline (+ short sublabel for non-compact bands) */}
      <header className="aai-fp__header">
        <ScoreBadge band={band} score={score?.fidelityScore} />
        <div className="aai-fp__headertext">
          <p className="aai-fp__headline">{meta.headline}</p>
          {meta.sublabel && (
            <p className="aai-fp__sublabel">{meta.sublabel}</p>
          )}
        </div>
      </header>

      {/* ISSUE LIST + DIFF — only on AMBER / RED (and GRAY shows errorReason) */}
      {(band === 'AMBER' || band === 'RED') && (
        <>
          {issues.length > 0 && (
            <ul className="aai-fp__issues" aria-label="Issues detected">
              {issues.map((iss, i) => (
                <li key={i} className="aai-fp__issue">{iss}</li>
              ))}
            </ul>
          )}
          {score?.transcriptDiff && (
            <DiffView diff={score.transcriptDiff} />
          )}
        </>
      )}

      {band === 'GRAY' && score?.errorReason && (
        <p className="aai-fp__error">{score.errorReason}</p>
      )}

      {/* ACTIONS */}
      <footer className="aai-fp__actions">
        <button
          type="button"
          className="aai-fp__btn aai-fp__btn--rerecord"
          onClick={onReRecord}
        >
          Re-record
        </button>

        {!acknowledging && (
          <button
            type="button"
            className="aai-fp__btn aai-fp__btn--save"
            disabled={!meta.canSubmit}
            onClick={handleSavePrimary}
          >
            {meta.saveLabel}
            {needsAcknowledge && (
              <span className="aai-fp__btnchevron" aria-hidden> ▾</span>
            )}
          </button>
        )}
      </footer>

      {/* ACKNOWLEDGEMENT — AMBER / RED, after first Save click */}
      {acknowledging && (
        <div className="aai-fp__ack" role="region" aria-label="Acknowledge and submit">
          <label className="aai-fp__acklabel" htmlFor="aai-fp-reason">
            Reason{reasonRequired ? ' (required)' : ' (optional)'}
          </label>
          <textarea
            id="aai-fp-reason"
            className="aai-fp__reason"
            placeholder={
              reasonRequired
                ? 'Briefly explain why this take should be submitted despite the divergence…'
                : 'Optional — e.g., "Stutter is intentional per direction."'
            }
            value={vaReason}
            onChange={(e) => setVaReason(e.target.value)}
            rows={2}
          />
          <div className="aai-fp__ackactions">
            <button
              type="button"
              className="aai-fp__btn aai-fp__btn--cancel"
              onClick={() => {
                setAcknowledging(false);
                setVaReason('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="aai-fp__btn aai-fp__btn--confirm"
              disabled={confirmDisabled}
              onClick={handleSavePrimary}
            >
              Confirm Save &amp; Submit
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default FidelityPanelMockup;
